import { Course, CourseOffer } from "@api"
import { ContentCopy, DragIndicator } from "@mui/icons-material"
import { Alert, Box, Button, IconButton, List, ListItem, ListItemButton, Paper, Snackbar, Typography } from "@mui/material"
import React, { useState, useCallback } from "react"
import { useNavigate } from "react-router-dom"
import { parseError } from "sprungkraft/helper/helper_functions"
import { useStore } from "sprungkraft/state/state"
import CourseOfferWidget from "sprungkraft/widgets/course_offer_widget"
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface CourseOffersProps {
    course: Course,
    reload: Function
}

interface SortableOfferItemProps {
    offer: CourseOffer;
    onEdit: (offer: CourseOffer) => void;
    onCopy: (offer: CourseOffer) => void;
}

const SortableOfferItem = ({ offer, onEdit, onCopy }: SortableOfferItemProps) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: offer.uuid });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    return (
        <ListItem
            ref={setNodeRef}
            style={style}
            key={offer.uuid}
            secondaryAction={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <IconButton
                        edge="end"
                        aria-label="drag"
                        {...attributes}
                        {...listeners}
                        sx={{ mr: 1, cursor: 'grab' }}
                    >
                        <DragIndicator />
                    </IconButton>
                    <IconButton edge="end" aria-label="duplicate" onClick={() => onCopy(offer)}>
                        <ContentCopy />
                    </IconButton>
                </Box>
            }
        >
            <ListItemButton onClick={() => onEdit(offer)} sx={{ mr: 8 }}>
                <CourseOfferWidget offer={offer} />
            </ListItemButton>
        </ListItem>
    );
};

const CourseOffers = ({ course, reload }: CourseOffersProps) => {
    const navigate = useNavigate();
    const { api: { courseOfferApi } } = useStore();
    const [copyError, setCopyError] = useState<string | null>(null);
    const [isUpdating, setIsUpdating] = useState<boolean>(false);

    // Sort offers by sort_index for consistent ordering
    const sortedOffers = [...course.offers].sort((a, b) => a.sort_index - b.sort_index);

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const handleDragEnd = useCallback(async (event: DragEndEvent) => {
        const { active, over } = event;

        if (over && active.id !== over.id) {
            const oldIndex = sortedOffers.findIndex(offer => offer.uuid === active.id);
            const newIndex = sortedOffers.findIndex(offer => offer.uuid === over.id);

            const reorderedOffers = arrayMove(sortedOffers, oldIndex, newIndex);

            // Update sort_index for all affected offers
            setIsUpdating(true);
            try {
                // Update sort indexes starting from the minimum affected index
                const minIndex = Math.min(oldIndex, newIndex);
                const updatesNeeded = reorderedOffers.slice(minIndex);

                const updatePromises = updatesNeeded.map((offer, index) => {
                    const newSortIndex = minIndex + index;
                    if (offer.sort_index !== newSortIndex) {
                        return courseOfferApi.changeCourseOffer({
                            courseOfferUuid: offer.uuid,
                            changeCourseOffer: {
                                sort_index: newSortIndex
                            }
                        }).toPromise();
                    }
                    return Promise.resolve();
                });

                await Promise.all(updatePromises);
                reload(); // Refresh the data
            } catch (error) {
                setCopyError(parseError(error));
            } finally {
                setIsUpdating(false);
            }
        }
    }, [sortedOffers, courseOfferApi, reload]);

    const handleAddOffer = () => {
        navigate(`/admin/course/${course.uuid}/add-offer`)
    }

    const handleEditOffer = (offer: CourseOffer) => {
        navigate(`/admin/course/${course.uuid}/${offer.uuid}/edit`)
    }

    const copyOffer = (offer: CourseOffer) => {
        courseOfferApi.addCourseOffer({
            addCourseOffer: {
                course_uuid: course.uuid!,
                enabled: false,
                num_slots: offer.num_slots,
                course_number: `${offer.course_number} (Kopie)`,
                official_course_id: offer.official_course_id,
                weekday_time_information: `${offer.weekday_time_information} (Kopie)`,
                dates_information: offer.dates_information,
                costs_information: offer.costs_information,
                place_information: offer.place_information,
            }
        }).subscribe({
            next: (course_offer) => {
                reload()
            }, error: (err) => {
                setCopyError(parseError(err))
            }
        })

    }

    return (<Paper sx={{ padding: 2 }}>
        <Typography variant="h6">
            Kursangebote {isUpdating && "(Sortierung wird aktualisiert...)"}
        </Typography>
        <Snackbar
            open={copyError != null}
            autoHideDuration={6000}
            onClose={() => setCopyError(null)}
            message={copyError}
        />
        <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
        >
            <List>
                {course.offers.length === 0 && <Alert severity="info">Noch kein Angebot. Fügen Sie ein ersten Angebot hinzu.</Alert>}
                {course.offers.length > 0 && (
                    <SortableContext
                        items={sortedOffers.map(offer => offer.uuid)}
                        strategy={verticalListSortingStrategy}
                    >
                        {sortedOffers.map((offer) => (
                            <SortableOfferItem
                                key={offer.uuid}
                                offer={offer}
                                onEdit={handleEditOffer}
                                onCopy={copyOffer}
                            />
                        ))}
                    </SortableContext>
                )}
            </List>
        </DndContext>
        <Button variant="outlined" onClick={handleAddOffer}>Angebot Hinzufügen</Button>
    </Paper>)
}

export default CourseOffers
